import React, { useContext, useEffect } from 'react'
import Helmet from 'react-helmet'
import { LayoutContext } from '../contexts'
import { usePageTracking, useIdentifyTracking } from '../hooks/tracking'
import { useGlobal } from '../hooks/useGlobal'

const SEO = ({
  location,
  title,
  description,
  image,
  isBlog,
  isBlogPost,
  isProduct,
  isPage,
  noIndex,
  canonicalLink = null,
  referrer = null,
  pageCategory,
  friendBuyWidgetId = null,
  schemas,
}) => {
  const { node_locale, alternateLang } = useContext(LayoutContext)

  const {
    layout: { seoFallbackImage, seoFallbackDescription },
  } = useGlobal(['layout'], node_locale)

  const ogImage = image || seoFallbackImage.file.url
  const seoDescription = description || seoFallbackDescription

  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'Pageview',
        pagePath: window.location.pathname,
        pageTitle: title,
      })
    }

    // page tracking is default on Segment
    // this will duplicate the call if you have trackPages: true

    usePageTracking({
      title,
      pageCategory,
    })
    // Segment automatically tracks anonymous id
    // no need to fire an extra one (messes up our checkout call)
    // useIdentifyTracking()
  }, [])

  const renderScriptFriendBuy = (merchantId) => (
    <script type='text/javascript'>
      {`
        // Double check that friendbuyAPI is initialized, loading script is included previously
        window["friendbuyAPI"] = friendbuyAPI = window["friendbuyAPI"] || [];
        friendbuyAPI.merchantId = "${merchantId}";
        friendbuyAPI.rootDomain = "stoxenergy.com";
        friendbuyAPI.push(["merchant", friendbuyAPI.merchantId]);
        (function(f, r, n, d, b, u, y) {
            while ((u = n.shift())) {
                (b = f.createElement(r)), (y = f.getElementsByTagName(r)[0]);
                b.async = 1;
                b.src = u;
                y.parentNode.insertBefore(b, y);
            }
        })(document, "script", [
            "https://static.fbot.me/friendbuy.js",
            "https://campaign.fbot.me/" + friendbuyAPI.merchantId + "/campaigns.js",
        ]);

        function getValByName(name) {
            var re = new RegExp(name + "=([^;]+)");
            var value = re.exec(document.cookie);
            return (value !== null) ? unescape(value[1]) : "";
        }
        const host = window.location.host;
        const urlParams = new URLSearchParams(window.location.search);
        const fbuy = urlParams.get("fbuy");
        if (fbuy) {
            document.cookie = "fby_fbuy=" + fbuy + ";domain=." + friendbuyAPI.rootDomain + ";path=/";
            document.cookie = "fby_hosts=" + host + ";domain=." + friendbuyAPI.rootDomain + ";path=/";
        }
        const fbuyVal = getValByName("fby_fbuy");
        const fbuyHosts = getValByName("fby_hosts").split(",");
        if (fbuyVal && fbuyHosts.indexOf(host) === -1) {
            friendbuyAPI.push(["setTracker", fbuyVal]);
            fbuyHosts.push(host);
            document.cookie = "fby_hosts=" + fbuyHosts.join(",") + ";domain=." + friendbuyAPI.rootDomain + ";path=/";
        }
      `}
    </script>
  )

  return (
    <>
      <Helmet>
        {/* General tags */}
        <html lang={node_locale} />
        <title>{title}</title>
        {alternateLang
          .filter((language) => language.node_locale !== node_locale)
          .map(({ node_locale, slug }) => (
            <link
              key={node_locale}
              rel='alternate'
              hrefLang={node_locale}
              href={`https://www.stoxenergy.com/${node_locale}/${
                slug ? slug + '/' : ''
              }`}
            />
          ))}
        <meta name='description' content={seoDescription} />
        <meta name='image' content={`https:${ogImage}`} />

        {/* FriendBuy integration */}
        <script type='text/javascript'>
          {`
             window['friendbuy'] = window['friendbuy'] || [];
             window['friendbuy'].push(['site', 'site-dac5236d-stoxenergy.com']);
             (function (f, r, n, d, b, y) {
                 b = f.createElement(r), y = f.getElementsByTagName(r)[0];b.async = 1;b.src = n;b.addEventListener('load', function(){${
                   friendBuyWidgetId
                     ? `window['friendbuy'].push(['widget', '${friendBuyWidgetId}']);`
                     : null
                 }});y.parentNode.insertBefore(b, y);
             })(document, 'script', '//djnf6e5yyirys.cloudfront.net/js/friendbuy.min.js');
          `}
        </script>
        {friendBuyWidgetId && (
          <script type='text/javascript'>{`
            window['friendbuy'] = window['friendbuy'] || [];
            window['friendbuy'].push(['widget', '${friendBuyWidgetId}']);
          `}</script>
        )}
        {location.pathname === '/nl/' && (
          <script type='text/javascript'>{`
            window.isContentfulMasterBackup = true;
          `}</script>
        )}
        {/**
         * Sitewide tracking for friendbuy
         * -Custom implementation from Friendbuy to surpass the change on domain-
         */}
        {node_locale !== 'en-US' && node_locale !== 'en-GB'
          ? renderScriptFriendBuy('ef015f5e-774c-4abc-8979-b476bd0aad0d')
          : ''}
        {node_locale === 'en-US'
          ? renderScriptFriendBuy('9f4859a6-23f4-48de-bf5f-088601622df5')
          : ''}
        {node_locale === 'en-GB'
          ? renderScriptFriendBuy('4eeff5c9-7727-4317-96e7-1bfa3359e0a6')
          : ''}
        {/* TraceDock integration */}
        {/* <script type='text/javascript'>
        {`
        !function(e,t,n,r,o,i,u,c,a,l){a=n.getElementsByTagName("head")[0],(l=n.createElement("script")).async=1,l.src=t,a.appendChild(l),r=n.cookie;try{if(i=(" "+r).match(new RegExp("[; ]_tdbu=([^\\s;]*)")))for(u in o=decodeURI(i[1]).split("||"))(c=o[u].split("~~"))[1]&&(r.indexOf(c[0]+"=")>-1||(n.cookie=c[0]+"="+c[1]+";path=/;max-age=604800;domain=."+e,n.cookie="_1=1"))}catch(e){}
        }("stoxenergy.com","https://db1.stoxenergy.com/main.js",document)
        `}
      </script> */}
        {/* OpenGraph tags */}
        <meta
          property='og:url'
          content={`https://www.stoxenergy.com${location.pathname}`}
        />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={seoDescription} />
        <meta property='og:image' content={`https:${ogImage}`} />
        {isPage && <meta property='og:type' content='page' />}
        {isBlog && <meta property='og:type' content='blog' />}
        {isBlogPost && <meta property='og:type' content='article' />}
        {isProduct && <meta property='og:type' content='product' />}
        {!noIndex && (
          <link
            rel='canonical'
            href={`https://www.stoxenergy.com${location.pathname}`}
          />
        )}
        {!noIndex && <meta name='robots' content='index' />}
        {noIndex && <meta name='robots' content='noindex' />}
        {schemas &&
          schemas.map((schema, index) => (
            <script key={index} type='application/ld+json'>
              {JSON.stringify(schema)}
            </script>
          ))}
      </Helmet>
    </>
  )
}

export default SEO
